import { parseIntegerInput } from "@/lib/parse-number";
import { gcd, modInverse } from "@/lib/tool-kit/ciphers/affine";
import {
  ALPHABET_SIZE,
  letterAt,
  letterIndex,
  lettersOnly,
  mod,
} from "@/lib/tool-kit/ciphers/alphabet";

/**
 * The Hill cipher (Lester S. Hill, 1929): blocks of n letters as vectors (A = 0 … Z = 25),
 * multiplied by an n × n key matrix modulo 26.
 *
 * Convention, because textbooks differ:
 *
 * - **column** — C = K · P, the plain text written as a column vector. Hill's own paper
 *   and Wikipedia work this way: with the key GYBNQKURP, ACT enciphers to POH.
 * - **row** — C = P · K, a row vector times the matrix (Stinson's *Cryptography: Theory
 *   and Practice*, for one). The same matrix gives a different cipher; it equals the
 *   column convention with the matrix transposed.
 *
 * A key works only if the matrix has an inverse modulo 26, which happens exactly when
 * its determinant shares no factor with 26 — it must be odd and not a multiple of 13.
 * The inverse is det⁻¹ · adj(K) mod 26, the adjugate method. Decrypting multiplies by it
 * the same way round as encrypting.
 */

export type HillSize = 2 | 3;
export type HillConvention = "column" | "row";
export type Matrix = number[][];

/** The first n² letters of a keyword, row by row, or null when there are too few. */
export function matrixFromKeyword(
  keyword: string,
  size: HillSize
): Matrix | null {
  const letters = lettersOnly(keyword);
  if (letters.length < size * size) {
    return null;
  }
  return Array.from({ length: size }, (_, row) =>
    Array.from({ length: size }, (_, column) =>
      letterIndex(letters.charAt(row * size + column))
    )
  );
}

/**
 * A key typed as n² boxes, row by row, or null while any box is not a whole number.
 * Entries are reduced mod 26: 29 is the same key entry as 3.
 */
export function matrixFromEntries(
  entries: readonly string[],
  size: HillSize
): Matrix | null {
  const values = entries.map((entry) => parseIntegerInput(entry));
  if (values.length !== size * size || values.some((value) => value === null)) {
    return null;
  }
  return reduceMatrix(
    Array.from({ length: size }, (_, row) =>
      values.slice(row * size, row * size + size).map((value) => value ?? 0)
    )
  );
}

/** Every entry reduced into 0–25. */
export function reduceMatrix(matrix: Matrix): Matrix {
  return matrix.map((row) => row.map((value) => mod(value, ALPHABET_SIZE)));
}

function minor(matrix: Matrix, skipRow: number, skipColumn: number): Matrix {
  return matrix
    .filter((_, row) => row !== skipRow)
    .map((row) => row.filter((_, column) => column !== skipColumn));
}

/** The exact (unreduced) determinant of a 1 × 1, 2 × 2 or 3 × 3 matrix. */
export function determinant(matrix: Matrix): number {
  if (matrix.length === 1) {
    return matrix[0][0];
  }
  if (matrix.length === 2) {
    return matrix[0][0] * matrix[1][1] - matrix[0][1] * matrix[1][0];
  }
  let result = 0;
  for (const [column, value] of matrix[0].entries()) {
    const sign = column % 2 === 0 ? 1 : -1;
    result += sign * value * determinant(minor(matrix, 0, column));
  }
  return result;
}

/** The adjugate: the transposed matrix of cofactors (exact, unreduced). */
export function adjugate(matrix: Matrix): Matrix {
  const size = matrix.length;
  if (size === 1) {
    return [[1]];
  }
  return Array.from({ length: size }, (_, row) =>
    Array.from({ length: size }, (_, column) => {
      // Entry (row, column) of the adjugate is the cofactor at (column, row).
      const sign = (row + column) % 2 === 0 ? 1 : -1;
      return sign * determinant(minor(matrix, column, row));
    })
  );
}

export interface Invertibility {
  /** The exact determinant. */
  det: number;
  /** det mod 26. */
  detMod: number;
  /** gcd(det mod 26, 26): 1 when invertible; 2, 13 or 26 when not. */
  sharedFactor: number;
  /** det⁻¹ mod 26, or null. */
  detInverse: number | null;
}

export function invertibility(matrix: Matrix): Invertibility {
  const det = determinant(matrix);
  const detMod = mod(det, ALPHABET_SIZE);
  return {
    det,
    detMod,
    sharedFactor: gcd(detMod, ALPHABET_SIZE),
    detInverse: modInverse(detMod, ALPHABET_SIZE),
  };
}

export interface MatrixInverse {
  detInverse: number;
  /** The adjugate, exact. */
  adjugate: Matrix;
  /** The adjugate reduced mod 26. */
  adjugateMod: Matrix;
  /** det⁻¹ · adj(K) mod 26. */
  inverse: Matrix;
}

/** K⁻¹ mod 26 by the adjugate method, or null when K has no inverse. */
export function inverseMatrix(matrix: Matrix): MatrixInverse | null {
  const { detInverse } = invertibility(matrix);
  if (detInverse === null) {
    return null;
  }
  const adjugateExact = adjugate(matrix);
  const adjugateMod = reduceMatrix(adjugateExact);
  return {
    detInverse,
    adjugate: adjugateExact,
    adjugateMod,
    inverse: reduceMatrix(
      adjugateMod.map((row) => row.map((value) => value * detInverse))
    ),
  };
}

/** The product A · B, exact. */
export function multiplyMatrices(a: Matrix, b: Matrix): Matrix {
  return a.map((row) =>
    b[0].map((_, column) =>
      row.reduce((sum, value, k) => sum + value * b[k][column], 0)
    )
  );
}

/** K · v (column) or v · K (row), exact. */
export function multiplyVector(
  matrix: Matrix,
  vector: readonly number[],
  convention: HillConvention
): number[] {
  const size = matrix.length;
  return Array.from({ length: size }, (_, i) => {
    let sum = 0;
    for (let k = 0; k < size; k += 1) {
      sum +=
        convention === "column"
          ? matrix[i][k] * vector[k]
          : vector[k] * matrix[k][i];
    }
    return sum;
  });
}

/**
 * When K has no inverse, the single entry to change — by the smallest step — that would
 * give it one. Null for a matrix that is already invertible.
 */
export function suggestInvertible(
  matrix: Matrix
): { row: number; column: number; value: number } | null {
  if (invertibility(matrix).detInverse !== null) {
    return null;
  }
  const size = matrix.length;
  // Bottom-right first, so the usual answer is "change the last entry".
  const cells = Array.from(
    { length: size * size },
    (_, i) => size * size - 1 - i
  );
  for (let step = 1; step < ALPHABET_SIZE; step += 1) {
    for (const cell of cells) {
      const row = Math.floor(cell / size);
      const column = cell % size;
      const found = [step, -step]
        .map((delta) => mod(matrix[row][column] + delta, ALPHABET_SIZE))
        .find((value) => {
          const changed = matrix.map((entries, r) =>
            entries.map((entry, c) =>
              r === row && c === column ? value : entry
            )
          );
          return invertibility(changed).detInverse !== null;
        });
      if (found !== undefined) {
        return { row, column, value: found };
      }
    }
  }
  return null;
}

export interface HillBlock {
  input: string;
  vector: number[];
  /** The product before reduction. */
  product: number[];
  /** The product mod 26. */
  reduced: number[];
  output: string;
}

export interface HillResult {
  output: string;
  blocks: HillBlock[];
  /** Encoding: filler letters added to complete the last block. */
  padded: number;
  /** Decoding: letters left over that do not fill a block. */
  leftover: string;
}

function runBlocks(
  letters: string,
  matrix: Matrix,
  convention: HillConvention
): HillBlock[] {
  const size = matrix.length;
  const blocks: HillBlock[] = [];
  for (let start = 0; start + size <= letters.length; start += size) {
    const input = letters.slice(start, start + size);
    const vector = [...input].map((letter) => letterIndex(letter));
    const product = multiplyVector(matrix, vector, convention);
    const reduced = product.map((value) => mod(value, ALPHABET_SIZE));
    blocks.push({
      input,
      vector,
      product,
      reduced,
      output: reduced.map((value) => letterAt(value)).join(""),
    });
  }
  return blocks;
}

/** Letters only, in capitals, padded with `filler` to a whole number of blocks. */
export function hillEncrypt(
  text: string,
  matrix: Matrix,
  convention: HillConvention,
  filler: string
): HillResult {
  const size = matrix.length;
  const letters = lettersOnly(text);
  const padded = (size - (letters.length % size)) % size;
  const blocks = runBlocks(letters + filler.repeat(padded), matrix, convention);
  return {
    output: blocks.map((block) => block.output).join(""),
    blocks,
    padded,
    leftover: "",
  };
}

/** Multiply by K⁻¹; null when the key has no inverse. */
export function hillDecrypt(
  text: string,
  matrix: Matrix,
  convention: HillConvention
): (HillResult & { inverse: MatrixInverse }) | null {
  const inverse = inverseMatrix(matrix);
  if (inverse === null) {
    return null;
  }
  const size = matrix.length;
  const letters = lettersOnly(text);
  const whole = letters.length - (letters.length % size);
  const blocks = runBlocks(
    letters.slice(0, whole),
    inverse.inverse,
    convention
  );
  return {
    output: blocks.map((block) => block.output).join(""),
    blocks,
    padded: 0,
    leftover: letters.slice(whole),
    inverse,
  };
}
